import React, { useEffect, useMemo, useState, useRef } from "react";
import { useMeasure, useMedia } from "react-use";
import styles from "./styles/QurealCanvas.module.css";
import { Button, Modal, message, Popconfirm } from "antd";
import { DeleteOutlined, MenuOutlined } from "@ant-design/icons";
import QurealOneViewer from "../../showwrapper/QurealOneViewer";
import QurealCanvasWrapper from "./QurealCanvasWrapper";
import CustomizerDesign from "./customizers/CustomizerDesign";
import CommentsBox from "./CommentBoxTemplate";
import { currentActionAtom } from "../../ai_home/atoms";
import { useSetAtom } from "jotai";
import { usePostProcessing } from "../../ai_img_gen/post_processing";
import { useCreateItemMutation } from "../../actions/app/items";
import CanvasFeedbackBar from "./CanvasFeedbackBar";
import { useReduceToken, useTokens, useBusiness } from "../../_actions/finance";
import TokenManagerModal from "../../finance/TokenManagerModal";
import { useQurealActions } from "qureal-editor";
import log from "cslog";
import {
    useDeleteItemMutation,
    useUpdateItemMutation,
} from "../../_actions/app/items";
import { queryClient } from "../../_helpers/query";
import { getEditURL } from "../../_helpers/editor";
import CanvasHeaderActions from "./CanvasHeaderActions";
import CanvasBar from "./CanvasBar";
import MobileCanvasActionBar from "./MobileCanvasActionBar";

function QurealCanvas({
    slug,
    is_template,
    item,
    prefill,
    isLoading = false,
    me,
    closeBtn,
    open_cust,
    setOpenCust,
    history_id,
    entry_id,
    linked_preset_id,
    platforms = [],
    modifications = {},
    devs = {},
    close = () => {},
}) {
    const [ref, { width, height }] = useMeasure();
    const { actionRef, download, share } = useQurealActions();
    const [status, setStatus] = useState({ download: false, share: false });
    const [org, setOrg] = useState(me?.selected_org?.id);
    const [person, setPerson] = useState(null);

    const mutationUpdate = useUpdateItemMutation();
    const deleteMutation = useDeleteItemMutation(() => {
        message.success("Entry deleted successfully!");
        queryClient.invalidateQueries(["items", "qdb_calendar_entries"]);
        close();
    });

    const [lang, setLang] = useState(modifications?.lang || null);
    const [prefill_data, setPrefillData] = useState(
        modifications?.prefill_data || prefill
    );
    const isInitialLangRender = useRef(true);
    const isInitialSaveRender = useRef(true);
    const isInitialUpdatedPrefillRender = useRef(true);
    const originalPrefill = useRef(prefill);

    const mutationTranslate = useBusiness(
        "gen/translate",
        (result) => {
            if (result?.success) {
                setPrefillData((old) => ({ ...old, ...result?.data }));
            }
        },
        () => {}
    );

    const [commentsOpen, setCommentsOpen] = useState(false);
    const [openTokenModal, setOpenTokenModal] = useState(false);
    const [deleteModalOpen, setDeleteModalOpen] = useState(false);
    const [active_slug, setActiveSlug] = useState(
        modifications?.template_id || slug
    );

    const { data: tokenData } = useTokens(me?.selected_org?.id);
    const reduceToken = useReduceToken(
        (result) => {
            log.d(result, "Token Reduced Successfully");
            if (
                result?.success === false &&
                result?.error?.code === "TOKEN_INSUFFICIENT"
            ) {
                message.error(result?.error?.msg);
                setOpenTokenModal(true);
            }
        },
        (error) => {
            log.e(error, "token reduction failed");
            const errorMessage =
                error?.response?.data?.detail ||
                error?.response?.data?.message ||
                error?.response?.data?.error ||
                error?.message ||
                "Failed to reduce token, Please try again.";
            message.error(errorMessage);
        }
    );

    const setCurrentAction = useSetAtom(currentActionAtom);
    const createCreationMutation = useCreateItemMutation((res) => {
        log.d(res, "resres");
        window.open(getEditURL(res.id), "_blank");
    });

    const isMobile = useMedia("(max-width: 700px)");
    const header_height = 50;

    const { updated_prefill } = usePostProcessing(
        prefill,
        modifications?.generated_images || {}
    );
    const [gs_overrides, setGSOverrides] = useState(
        modifications?.gs_overrides || { config: {} }
    );

    const is_internal_user = ["designer", "reviewer"].includes(me?.irole);

    const memoizedPrefill = useMemo(
        () => preparePrefill(prefill_data),
        [prefill_data]
    );

    log.d(updated_prefill, "d123 updated_prefill");
    log.d(prefill_data, "d123 prefill_data");

    useEffect(() => {
        setActiveSlug(slug);
    }, [slug]);

    useEffect(() => {
        if (isInitialUpdatedPrefillRender.current) {
            isInitialUpdatedPrefillRender.current = false;
            return;
        }
        if (updated_prefill) {
            Object.keys(updated_prefill).forEach((block_id) => {
                const block = updated_prefill[block_id];
                if (block) {
                    setPrefillData((prev) => ({
                        ...prev,
                        [block_id]: { ...prev[block_id], ...block },
                    }));
                }
            });
        }
    }, [updated_prefill]);

    useEffect(() => {
        if (isInitialLangRender.current) {
            isInitialLangRender.current = false;
            return;
        }
        if (!lang || lang === "English") {
            setPrefillData(originalPrefill.current);
            return;
        }
        if (Object.keys(prefill_data || {}).length === 0) return;
        const out = {};
        Object.keys(prefill_data).forEach((input_id) => {
            if (input_id !== "history_id") {
                out[input_id] = prefill_data[input_id];
            }
        });
        mutationTranslate.mutate({ data: out, lang });
    }, [lang]);

    useEffect(() => {
        if (isInitialSaveRender.current) {
            isInitialSaveRender.current = false;
            return;
        }
        if (entry_id) {
            mutationUpdate.mutate({
                collection: "qdb_calendar_entries",
                id: entry_id,
                data: {
                    modifications: {
                        lang,
                        template_id: active_slug,
                        gs_overrides,
                        prefill_data,
                        generated_images: updated_prefill,
                    },
                },
            });
        }
    }, [lang, active_slug, gs_overrides, prefill_data, entry_id]);

    log.d(item, "item in QCV");

    return (
        <>
            <QurealCanvasWrapper
                title={item.title}
                subtitle={item?.types?.[0]?.qdb_creation_type_id?.name}
                actions_left={[
                    !isMobile && (
                        <Button
                            key="menu"
                            icon={<MenuOutlined />}
                            onClick={() => setOpenCust(!open_cust)}
                        />
                    ),
                ]}
                actions_extra={
                    !isMobile
                        ? [
                              <CanvasHeaderActions
                                  key="canvas-header-actions"
                                  item={item}
                                  me={me}
                                  isMobile={isMobile}
                                  is_internal_user={is_internal_user}
                                  is_template={is_template}
                                  tokenData={tokenData}
                                  setOpenTokenModal={setOpenTokenModal}
                                  setCurrentAction={setCurrentAction}
                                  reduceToken={reduceToken}
                                  createCreationMutation={
                                      createCreationMutation
                                  }
                                  updated_prefill={updated_prefill}
                                  setCommentsOpen={setCommentsOpen}
                                  devs={devs}
                                  lang={lang}
                                  active_slug={active_slug}
                                  gs_overrides={gs_overrides}
                                  prefill_data={prefill_data}
                              />,
                          ]
                        : []
                }
                actions_basics={[
                    isMobile && (
                        <CanvasFeedbackBar
                            key="feedback-inline"
                            history_id={history_id}
                            entry_id={entry_id}
                            inline={true}
                        />
                    ),
                    closeBtn,
                ]}
                header_height={header_height}
                controlPanel={
                    <>
                        {isMobile && (
                            <MobileCanvasActionBar
                                tokenData={tokenData}
                                setOpenTokenModal={setOpenTokenModal}
                                setCurrentAction={setCurrentAction}
                                reduceToken={reduceToken}
                                share={share}
                                download={download}
                                status={status}
                                onDelete={
                                    entry_id
                                        ? () => setDeleteModalOpen(true)
                                        : null
                                }
                            />
                        )}
                        <div className={styles.control_panel_wrapper}>
                            <CustomizerDesign
                                item={item}
                                id={item.id}
                                me={me}
                                isMobile={isMobile}
                                prefill={prefill_data}
                                setPrefill={setPrefillData}
                                gs_overrides={gs_overrides}
                                setGSOverrides={setGSOverrides}
                                org={org}
                                setOrg={setOrg}
                                person={person}
                                setPerson={setPerson}
                                lang={lang}
                                setLang={setLang}
                                isTranslating={mutationTranslate.isLoading}
                                linked_preset_id={linked_preset_id}
                                platforms={platforms}
                                entry_id={entry_id}
                            />
                        </div>
                    </>
                }
                open_cp={open_cust}
                setOpenCp={setOpenCust}
                bottom_panel={
                    entry_id && !isMobile ? (
                        <div
                            style={{
                                display: "flex",
                                flexDirection: "row",
                                justifyContent: "flex-end",
                            }}
                        >
                            <Popconfirm
                                title="Delete entry"
                                description="Are you sure you want to delete this entry?"
                                onConfirm={() =>
                                    deleteMutation.mutate({
                                        collection: "qdb_calendar_entries",
                                        id: entry_id,
                                    })
                                }
                                okText="Yes"
                                cancelText="No"
                                okButtonProps={{ danger: true }}
                            >
                                <Button
                                    danger
                                    icon={<DeleteOutlined />}
                                    loading={deleteMutation.isLoading}
                                >
                                    Delete
                                </Button>
                            </Popconfirm>
                        </div>
                    ) : null
                }
            >
                <div
                    ref={ref}
                    style={{
                        height: isMobile ? "100vw" : "100%",
                        maxWidth: "100%",
                    }}
                    key={`${slug}-${width}-${height}-${
                        open_cust ? "open" : "close"
                    }`}
                >
                    {!isMobile && (
                        <CanvasBar
                            lang={lang}
                            setLang={setLang}
                            isTranslating={mutationTranslate.isLoading}
                            prefill={prefill_data}
                            setPrefill={setPrefillData}
                            gs_overrides={gs_overrides}
                            setGSOverrides={setGSOverrides}
                            me={me}
                            item={item}
                            isMobile={isMobile}
                            share={share}
                            download={download}
                            status={status}
                        />
                    )}
                    <QurealOneViewer
                        key={`${slug}-${width}-${height}-${
                            open_cust ? "open" : "close"
                        }`}
                        slug={active_slug}
                        type={is_template ? "template" : "creation"}
                        creation_type={item.type}
                        preview={true}
                        isMobile={isMobile}
                        prefill={memoizedPrefill}
                        style={{
                            // height: height - header_height - (isMobile ? -5 : 10),
                            height: height - (isMobile ? -5 : 10),
                            outline: "none",
                            border: "1px solid transparent",
                            padding: isMobile ? "10px 0" : "10px",
                        }}
                        setStatus={setStatus}
                        actionRef={actionRef}
                        status={status}
                        organization={org}
                        gs_overrides={gs_overrides}
                    />
                    {!isMobile && (
                        <CanvasFeedbackBar
                            history_id={history_id}
                            entry_id={entry_id}
                        />
                    )}
                </div>
                <Modal
                    open={deleteModalOpen}
                    onCancel={() => setDeleteModalOpen(false)}
                    title="Delete entry"
                    okText="Delete"
                    okButtonProps={{
                        danger: true,
                        loading: deleteMutation.isLoading,
                    }}
                    cancelText="Cancel"
                    onOk={() => {
                        deleteMutation.mutate({
                            collection: "qdb_calendar_entries",
                            id: entry_id,
                        });
                        setDeleteModalOpen(false);
                    }}
                    centered
                >
                    Are you sure you want to delete this entry?
                </Modal>
                <Modal
                    open={commentsOpen}
                    onCancel={() => setCommentsOpen(false)}
                    footer={null}
                    title="Comments"
                    width={700}
                >
                    {item?.id ? (
                        <CommentsBox template={item} />
                    ) : (
                        <div>Loading...</div>
                    )}
                </Modal>
            </QurealCanvasWrapper>
            <TokenManagerModal
                selected_org={me?.selected_org?.id}
                open={openTokenModal}
                onClose={() => setOpenTokenModal(false)}
            />
        </>
    );
}

export default QurealCanvas;

function preparePrefill(data) {
    if (!data || typeof data !== "object") return data;
    const result = {};
    for (const key in data) {
        if (Object.prototype.hasOwnProperty.call(data, key)) {
            const val = data[key];
            if (
                val &&
                typeof val === "object" &&
                val.success === true &&
                val.data
            ) {
                result[key] = val.data;
            } else {
                result[key] = val;
            }
        }
    }
    return result;
}
